import classNames from 'classnames';
import SVG from 'react-inlinesvg';
import Tooltip, { TooltipDirection, TooltipTheme } from 'lib/Tooltip';
import { ButtonGroupOption, ButtonGroupProps, Height, Padding, Theme, Width } from './ButtonGroup.types';

const cx = classNames;

const renderButtonContent = ({
  option,
  isSelected,
}: {
  option: ButtonGroupOption;
  isSelected: boolean;
}): React.ReactElement | string => {
  const tooltipDirection = option.icon ? TooltipDirection.TOP : TooltipDirection.BOTTOM;
  const tooltipText = option.icon ? option.text : option.tooltip;

  const innerContent = option.icon ? (
    <div className="flex gap-1" data-test-id={option.value}>
      <div className="px-1.5 py-[1px]" data-test-id={`icon-${option.value}`}>
        <SVG
          src={option.icon}
          className={cx('h-4 w-4', { 'text-primary-900': isSelected, 'text-neutral-950-opacity-50': !isSelected })}
        />
      </div>
    </div>
  ) : (
    <span className="whitespace-nowrap">{option.text}</span>
  );

  if (option.icon || option.tooltip) {
    return (
      <Tooltip direction={tooltipDirection} theme={TooltipTheme.DARK} tooltip={tooltipText}>
        {innerContent}
      </Tooltip>
    );
  }

  return option.text;
};

const ButtonGroup = ({
  onChange,
  options,
  selectedValue,
  dataTestId,
  icon,
  padding = Padding.LOOSE,
  theme = Theme.DARK,
  isDisabled = false,
  width = Width.MIN,
  height = Height.LARGE,
  testIdSource = 'index',
}: ButtonGroupProps): React.ReactElement => {
  return (
    <div
      className={cx('flex-1 inline-flex items-center p-0.5 gap-0.5 rounded-lg', {
        'bg-primary': theme === Theme.DARK,
        'bg-neutral-100': theme === Theme.LIGHT,
        'bg-neutral-950-opacity-5': theme === Theme.SUBTLE,
        'w-full': width === Width.MAX,
        'w-auto': width === Width.MIN,
      })}
      data-test-id={dataTestId}
    >
      {icon && (
        <SVG
          src={icon}
          className={cx('w-4 h-4 ml-2.5 mr-2', {
            'text-neutral-100-opacity-40': theme === Theme.DARK && isDisabled,
            'text-surfaceLight-disabled': (theme === Theme.LIGHT || theme === Theme.SUBTLE) && isDisabled,
            'text-neutral-100': theme === Theme.DARK,
            'text-neutral-900': theme === Theme.LIGHT,
            'text-primary-900': theme === Theme.SUBTLE,
          })}
        />
      )}
      {options.map((option, idx) => {
        const isSelected = option.value === selectedValue;

        return (
          <button
            type="button"
            key={option.value}
            data-pendo-id={option.dataPendoId}
            data-test-id={
              testIdSource === 'index' ? `button-group-${idx}` : option.text.toLowerCase().replace(/\s+/g, '-')
            }
            onClick={() => onChange(option.value)}
            className={cx(
              'flex items-center flex-1 rounded-md body-base disabled:cursor-not-allowed',
              {
                'px-4': padding === Padding.LOOSE,
              },
              {
                'px-1': padding === Padding.TIGHT,
              },
              {
                'py-2.5': height === Height.LARGE,
              },
              {
                'py-1.5': height === Height.MEDIUM,
              },
              {
                'py-1': height === Height.SMALL,
              },
              {
                'py-0': height === Height.EXTRA_SMALL,
              },
              {
                'body-base': !isSelected,
              },
              {
                'sub-header-base': isSelected,
              },
              {
                'disabled:text-neutral-100-opacity-40': isDisabled && theme === Theme.DARK,
              },
              {
                'hover:opacity-70 text-neutral-100': !isDisabled && !isSelected && theme === Theme.DARK,
              },
              {
                'bg-neutral-100-opacity-50 text-neutral-100 disabled:text-neutral-100-opacity-40':
                  isSelected && theme === Theme.DARK,
              },
              {
                'disabled:opacity-40': option.isDisabled && theme === Theme.LIGHT,
              },
              {
                'disabled:text-surfaceLight-disabled': isDisabled && theme === Theme.LIGHT,
              },
              {
                'text-neutral-900 border border-transparent': !isDisabled && !isSelected && theme === Theme.LIGHT,
              },
              {
                'bg-neutral-50 text-neutral-950 border border-neutral-300': isSelected && theme === Theme.LIGHT,
              },
              {
                'disabled:opacity-40': option.isDisabled && theme === Theme.SUBTLE,
              },
              {
                'disabled:text-surfaceLight-disabled': isDisabled && theme === Theme.SUBTLE,
              },
              {
                'text-primary-900 border border-transparent': !isDisabled && !isSelected && theme === Theme.SUBTLE,
              },
              {
                'bg-white text-primary-900 border border-neutral-300': isSelected && theme === Theme.SUBTLE,
              },
              {
                'disabled:text-neutral-400': isDisabled && !isSelected && theme === Theme.SUBTLE,
              },
            )}
            disabled={isDisabled || option.isDisabled}
          >
            {renderButtonContent({ option, isSelected })}
          </button>
        );
      })}
    </div>
  );
};

export default ButtonGroup;
